/**
 * User Mutation Hooks
 *
 * React Query mutations for avatar upload/delete and profile updates.
 * Avatar data lives in auth context, so mutations call updateUser()
 * directly with the response data for instant UI feedback.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { authService } from '@/features/auth/services/auth-service';
import { userService } from '@/shared/services/user-service';
import { householdMemberKeys } from '@/shared/hooks/use-household-members';
import { balanceKeys } from '@/features/settle-up/hooks/use-balances';
import { transactionKeys } from '@/features/transactions/hooks/use-transactions';
import type { UserUpdate } from '@/shared/types';

export const userKeys = {
  all: ['user'] as const,
  me: () => [...userKeys.all, 'me'] as const,
};

export function useUploadAvatar() {
  const { updateUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => userService.uploadAvatar(file),
    onSuccess: (data) => {
      updateUser({ avatar_url: data.avatar_url });
      queryClient.invalidateQueries({ queryKey: householdMemberKeys.all });
    },
  });
}

export function useDeleteAvatar() {
  const { updateUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => userService.deleteAvatar(),
    onSuccess: () => {
      updateUser({ avatar_url: null });
      queryClient.invalidateQueries({ queryKey: householdMemberKeys.all });
    },
  });
}

export function useUpdateAvatarColor() {
  const { updateUser } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (avatarColor: string) =>
      userService.updateProfile({ avatar_color: avatarColor }),
    onSuccess: (data) => {
      updateUser({ avatar_color: data.avatar_color });
      queryClient.invalidateQueries({ queryKey: householdMemberKeys.all });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpdate }) =>
      userService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: householdMemberKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: balanceKeys.all });
      queryClient.invalidateQueries({ queryKey: transactionKeys.all });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: householdMemberKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: balanceKeys.all });
      queryClient.invalidateQueries({ queryKey: transactionKeys.all });
    },
  });
}

export function useDeleteMyAccount() {
  return useMutation({
    mutationFn: (password: string) => authService.deleteMyAccount(password),
  });
}

export function useEnableHouseholdSharing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => userService.enableHouseholdSharing(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: householdMemberKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['household', 'current'] });
    },
  });
}

export function useDisableHouseholdSharing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => userService.disableHouseholdSharing(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: householdMemberKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['household', 'current'] });
    },
  });
}

export function useRespondToShareRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accept: boolean) => userService.respondToShareRequest(accept),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: householdMemberKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['household', 'current'] });
    },
  });
}
